import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { Form, Formik } from "formik";
import { useNavigate, useParams } from "react-router-dom";

import AddStepEditor from "./components/AddStepEditor";
import StepsSidebar from "./components/StepsSideBar";
import WorkflowHeaderForm from "./components/WorkflowHeaderForm";
import WorkflowFlowPreview from "./components/WorkflowFlowPreview";
import { workflowConfigSchema } from "./helper/WorkflowConfigSchema";
import { buildWorkflowSavePayload } from "./helper/WorkflowConfigPayload";
import { useGetAllRolesQuery } from "store/slices/apis/rolesSlice";
import { useAddWorkflowConfigurationMutation } from "store/slices/apis/workflowConfigrationSlice";
import { useDispatch } from "store";
import { closeSnackbar, openSnackbar } from "store/slices/snackbar";

const AddWorkflowConfiguration = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { projectId } = useParams();
  const [selectedStepIndex, setSelectedStepIndex] = useState(0);

  const [formData] = useState({
    workflowName: "",
    entityName: "",
    description: "",
    isActive: false,
    projectId,
    tenantId: null,
    isDefault: true,
    priority: 0,
  });

  const { data: rolesRes } = useGetAllRolesQuery(
    { projectId },
    { skip: !projectId }
  );

  const roles = useMemo(() => {
    const list = rolesRes?.data ?? rolesRes ?? [];
    return list.map((role) => ({
      value: role.id ?? role.value,
      label: role.displayName ?? role.label,
    }));
  }, [rolesRes]);

  const [addWorkflowConfiguration, addWorkflowRes] =
    useAddWorkflowConfigurationMutation();

  React.useEffect(() => {
    if (addWorkflowRes?.isSuccess) {
      if (addWorkflowRes?.data?.status?.toLowerCase() == "success") {
        openNotification(addWorkflowRes?.data?.message, "success");
        navigate(`/projects/${projectId}/settings/workflow`);
      } else {
        openNotification(addWorkflowRes?.data?.message, "error");
      }
    }
  }, [addWorkflowRes?.isSuccess]);

  React.useEffect(() => {
    if (addWorkflowRes?.isError) {
      if (addWorkflowRes?.error?.data?.status?.toLowerCase() == "error ") {
        openNotification(
          addWorkflowRes?.error?.data?.message ?? "Something went wrong!",
          "error"
        );
      } else {
        openNotification("Something went wrong!", "error");
      }
    }
  }, [addWorkflowRes?.isError]);

  const openNotification = (msg, type = "error", duration = 3000) => {
    dispatch(
      openSnackbar({
        open: true,
        message: msg,
        variant: "alert",
        alert: {
          color: type,
        },
        close: false,
      })
    );
    setTimeout(() => dispatch(closeSnackbar()), duration);
  };

  return (
    <Grid container spacing={2} sx={{ p: 2 }}>
      <Grid size={12}>
        <Formik
          enableReinitialize
          initialValues={formData}
          validationSchema={workflowConfigSchema}
          onSubmit={async (values) => {
            const payload = buildWorkflowSavePayload(values);
            await addWorkflowConfiguration(payload).unwrap();
          }}
        >
          {({ dirty, isValid, values }) => (
            <Form>
              <Grid container spacing={2}>
                <Grid size={12}>
                  <Paper
                    sx={{
                      p: { xs: 2, md: 3 },
                      borderRadius: 3,
                      border: "1px solid #D9E4F2",
                      background:
                        "linear-gradient(135deg, #F8FBFF 0%, #FFFFFF 48%, #F7FBF8 100%)",
                    }}
                  >
                    <Stack
                      direction={{ xs: "column", md: "row" }}
                      justifyContent="space-between"
                      alignItems={{ xs: "flex-start", md: "center" }}
                      spacing={2}
                      sx={{ mb: 2.5 }}
                    >
                      <Box>
                        <Typography
                          sx={{
                            fontWeight: 600,
                            fontSize: { xs: "16px", sm: "18px" },
                            lineHeight: "24px",
                            color: "#4A5A70",
                            fontFamily: "Outfit, sans-serif",
                          }}
                        >
                          Config & Settings
                        </Typography>
                        <Typography
                          sx={{
                            fontWeight: 700,
                            fontSize: { xs: "24px", sm: "32px", md: "38px" },
                            lineHeight: { xs: "32px", md: "44px" },
                            color: "#252C32",
                            fontFamily: "Outfit, sans-serif",
                          }}
                        >
                          Build Workflow Configuration
                        </Typography>
                        <Typography
                          sx={{
                            mt: 0.75,
                            fontSize: "15px",
                            lineHeight: "24px",
                            color: "#5F6B84",
                            fontFamily: "Outfit, sans-serif",
                            maxWidth: "780px",
                          }}
                        >
                          Define the workflow details, visualize how it will
                          flow, and configure each step from a builder-style
                          workspace.
                        </Typography>
                      </Box>

                      <Stack direction="row" spacing={1}>
                        <Button variant="outlined" onClick={() => navigate(-1)}>
                          Cancel
                        </Button>
                        <Button
                          type="submit"
                          variant="contained"
                          disabled={
                            !dirty || !isValid || addWorkflowRes?.isLoading
                          }
                        >
                          Save Workflow
                        </Button>
                      </Stack>
                    </Stack>

                    <Stack direction="row" flexWrap="wrap" gap={1}>
                      <Chip
                        label={values.workflowName?.trim() || "Untitled workflow"}
                        sx={{
                          bgcolor: "#EEF4FF",
                          color: "#0A65E4",
                          fontFamily: "Outfit, sans-serif",
                          fontWeight: 600,
                        }}
                      />
                      <Chip
                        label={values.entityName || "No entity selected"}
                        sx={{
                          bgcolor: "#F4F7FB",
                          color: "#5F6B84",
                          fontFamily: "Outfit, sans-serif",
                          fontWeight: 600,
                        }}
                      />
                      <Chip
                        label={`${values?.steps?.length || 0} ${
                          (values?.steps?.length || 0) === 1 ? "Step" : "Steps"
                        }`}
                        sx={{
                          bgcolor: "#EAF7EE",
                          color: "#237B4B",
                          fontFamily: "Outfit, sans-serif",
                          fontWeight: 600,
                        }}
                      />
                      <Chip
                        label={values.isActive ? "Active" : "Inactive"}
                        sx={{
                          bgcolor: values.isActive ? "#EAF7EE" : "#FFF4DE",
                          color: values.isActive ? "#237B4B" : "#B76E00",
                          fontFamily: "Outfit, sans-serif",
                          fontWeight: 600,
                        }}
                      />
                    </Stack>
                  </Paper>
                </Grid>

                <Grid size={12}>
                  <Paper
                    sx={{
                      p: { xs: 2, md: 3 },
                      borderRadius: 3,
                      border: "1px solid #E4EAF2",
                    }}
                  >
                    <Stack spacing={2}>
                      <Box>
                        <Typography
                          sx={{
                            fontSize: "20px",
                            fontWeight: 700,
                            color: "#252C32",
                            fontFamily: "Outfit, sans-serif",
                          }}
                        >
                          Workflow Basics
                        </Typography>
                        <Typography
                          sx={{
                            mt: 0.5,
                            fontSize: "14px",
                            color: "#6B7280",
                            fontFamily: "Outfit, sans-serif",
                          }}
                        >
                          Start with the overall workflow identity, then move
                          into the builder to configure steps and routing.
                        </Typography>
                      </Box>

                      <WorkflowHeaderForm roles={roles} />
                    </Stack>
                  </Paper>
                </Grid>

                <Grid size={{ xs: 12, md: 4, lg: 3.2 }}>
                  <Paper
                    sx={{
                      p: 2,
                      borderRadius: 3,
                      border: "1px solid #E4EAF2",
                      height: "100%",
                      minHeight: 640,
                    }}
                  >
                    <Stack spacing={2} sx={{ height: "100%" }}>
                      <Box>
                        <Typography
                          sx={{
                            fontSize: "20px",
                            fontWeight: 700,
                            color: "#252C32",
                            fontFamily: "Outfit, sans-serif",
                          }}
                        >
                          Workflow Outline
                        </Typography>
                        <Typography
                          sx={{
                            mt: 0.5,
                            fontSize: "14px",
                            color: "#6B7280",
                            fontFamily: "Outfit, sans-serif",
                          }}
                        >
                          Add steps, reorder them, and select the one you want
                          to configure.
                        </Typography>
                      </Box>

                      <Divider />

                      <Box sx={{ flex: 1, overflow: "auto", pr: 0.5 }}>
                        <StepsSidebar
                          roles={roles}
                          selectedStepIndex={selectedStepIndex}
                          onSelect={setSelectedStepIndex}
                        />
                      </Box>
                    </Stack>
                  </Paper>
                </Grid>

                <Grid size={{ xs: 12, md: 8 }}>
                  <Paper
                    sx={{
                      p: 2,
                      borderRadius: 3,
                      border: "1px solid #E4EAF2",
                      minHeight: { xs: "auto", md: 520, lg: 640 },
                    }}
                  >
                    <Stack spacing={2} sx={{ height: "100%" }}>
                      <Box>
                        <Typography
                          sx={{
                            fontSize: "20px",
                            fontWeight: 700,
                            color: "#252C32",
                            fontFamily: "Outfit, sans-serif",
                          }}
                        >
                          Step Designer
                        </Typography>
                        <Typography
                          sx={{
                            mt: 0.5,
                            fontSize: "14px",
                            color: "#6B7280",
                            fontFamily: "Outfit, sans-serif",
                          }}
                        >
                          As soon as you add a step, configure its name, role,
                          approval and rejection behavior, and child steps here.
                        </Typography>
                      </Box>

                      <Divider />

                      <Box sx={{ flex: 1, overflow: "auto", pr: 0.5 }}>
                        <AddStepEditor
                          roles={roles}
                          selectedStepIndex={selectedStepIndex}
                          onSelectStep={setSelectedStepIndex}
                        />
                      </Box>
                    </Stack>
                  </Paper>
                </Grid>

                <Grid size={12}>
                  <Paper
                    sx={{
                      p: 2,
                      borderRadius: 3,
                      border: "1px solid #D9E4F2",
                      background:
                        "linear-gradient(180deg, #FCFDFE 0%, #F8FBFF 100%)",
                      minHeight: 640,
                    }}
                  >
                    <Stack spacing={2}>
                      <Box>
                        <Typography
                          sx={{
                            fontSize: "20px",
                            fontWeight: 700,
                            color: "#252C32",
                            fontFamily: "Outfit, sans-serif",
                          }}
                        >
                          Visual Builder
                        </Typography>
                        <Typography
                          sx={{
                            mt: 0.5,
                            fontSize: "14px",
                            color: "#6B7280",
                            fontFamily: "Outfit, sans-serif",
                          }}
                        >
                          Review the workflow structure visually while you build
                          it, including decision branches, child steps, and
                          parallel paths.
                        </Typography>
                      </Box>

                      <Box
                        sx={{
                          borderRadius: 2.5,
                          border: "1px solid #E4EAF2",
                          backgroundColor: "#FFFFFF",
                          p: { xs: 1, md: 2 },
                        }}
                      >
                        <WorkflowFlowPreview roles={roles} />
                      </Box>
                    </Stack>
                  </Paper>
                </Grid>

              </Grid>
            </Form>
          )}
        </Formik>
      </Grid>
    </Grid>
  );
};

export default AddWorkflowConfiguration;
